import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { filterSowLessonOptions } from '../../utils/sowLessons';

const subStrandText = (row) => {
    const value = row?.sub_strand;
    return Array.isArray(value) ? value.filter(Boolean).join(', ') : (value || '');
};

/**
 * Lesson title box that also searches the Scheme of Work.
 * The teacher can type any title; matching SoW lessons are suggested and picking one calls onSelect(option).
 * `options` are the SoW lessons already narrowed to where the modal was opened from (see buildSowLessonOptions).
 */
const LessonTitleSearch = ({
    value,
    onChange,
    options,
    onSelect,
    placeholder = 'Enter title for new plan...',
    emptyMessage = 'No scheme of work lessons found. Type a custom title.',
    disabledMessage = '',
    inputClassName = '',
    autoValue = ''
}) => {
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(0);
    const containerRef = useRef(null);

    // Text the form filled in by itself (e.g. "Lesson Plan: <sub-strand>" or the chosen lesson's title) is not a search
    const query = value === autoValue ? '' : value;

    const visible = useMemo(() => filterSowLessonOptions(options, { query }), [options, query]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => { setHighlighted(0); }, [query, open]);

    const choose = (option) => {
        onSelect(option);
        setOpen(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setOpen(false);
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setHighlighted(h => Math.min(h + 1, Math.max(visible.length - 1, 0)));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted(h => Math.max(h - 1, 0));
        } else if (e.key === 'Enter' && open && visible[highlighted]) {
            e.preventDefault();
            choose(visible[highlighted]);
        }
    };

    return (
        <div ref={containerRef} className="relative">
            <div className="relative">
                <input
                    type="text"
                    value={value}
                    onChange={(e) => {
                        onChange(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={(e) => {
                        // Select the text so the first letter typed replaces an automatic title instead of extending it
                        if (value === autoValue) e.target.select();
                        setOpen(true);
                    }}
                    onKeyDown={handleKeyDown}
                    className={`w-full px-4 py-3 pr-11 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all font-medium text-[#18216D] ${inputClassName}`}
                    placeholder={placeholder}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls="lesson-title-suggestions"
                    aria-autocomplete="list"
                />
                <MagnifyingGlassIcon className="w-5 h-5 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>

            {open && (
                <div className="absolute z-30 left-0 right-0 mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl max-h-60 overflow-y-auto custom-scrollbar" role="listbox" id="lesson-title-suggestions">
                    {disabledMessage ? (
                        <p className="px-4 py-3 text-xs font-bold text-slate-400">{disabledMessage}</p>
                    ) : visible.length === 0 ? (
                        <p className="px-4 py-3 text-xs font-bold text-slate-400">{emptyMessage}</p>
                    ) : (
                        visible.map((option, index) => (
                            <button
                                key={`${option.week}-${option.lessonLabel}-${index}`}
                                type="button"
                                role="option"
                                aria-selected={index === highlighted}
                                onMouseEnter={() => setHighlighted(index)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => choose(option)}
                                className={`w-full text-left px-4 py-2.5 flex flex-col gap-0.5 transition-colors ${index === highlighted ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                            >
                                <span className="text-sm font-bold text-[#18216D]">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-blue-600 mr-2">
                                        Week {option.week} · {option.lessonLabel}
                                    </span>
                                    {option.title || 'Untitled lesson'}
                                </span>
                                {subStrandText(option.row) && (
                                    <span className="text-[11px] font-medium text-slate-400 truncate">{subStrandText(option.row)}</span>
                                )}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default LessonTitleSearch;
